import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { canView } from '@/shared/lib/permissions';
import { useAuthStore } from '@/shared/stores/auth';
import { Tabs } from '@/components/ui';
import { Icon, type IconName } from '@/components/ui/Icon';
import { Dispositions } from './Dispositions';
import { CallTags } from './CallTags';

/**
 * הגדרות תיוג וסיום שיחה: קודי סיום + תגיות שיחה בלשוניות
 * (/admin/dispositions). הנתיב הישן /admin/call-tags מפנה לכאן.
 * הלשוניות: בורר מקטעים (עיצוב v2) עם אייקון.
 */
const ALL_TABS: { id: string; label: string; icon: IconName; perm: string }[] = [
  { id: 'dispositions', label: 'קודי סיום', icon: 'ok', perm: 'dispositions' },
  { id: 'tags', label: 'תגיות שיחה', icon: 'tag', perm: 'call_tags' },
];

/**
 * embedded = מוטמע כלשונית בתוך מסך אחר (הגדרות): הלשוניות הפנימיות נשמרות
 * ב-state ולא ב-?tab= — שכבר שייך למסך העוטף. initialTab מגיע מ-?sub=.
 */
export function TaggingCenter({ embedded = false, initialTab }: { embedded?: boolean; initialTab?: string } = {}) {
  const [params, setParams] = useSearchParams();
  const [localTab, setLocalTab] = useState(initialTab ?? 'dispositions');
  const user = useAuthStore((s) => s.user);
  const tabs = ALL_TABS.filter((t) => canView(user, t.perm));
  const requested = embedded ? localTab : (params.get('tab') ?? 'dispositions');
  const tab = tabs.some((t) => t.id === requested) ? requested : (tabs[0]?.id ?? 'dispositions');
  const select = (id: string) => (embedded ? setLocalTab(id) : setParams(id === 'dispositions' ? {} : { tab: id }, { replace: true }));

  // מוטמע בהגדרות: בלי כותרת (שם הלשונית כבר בתת-התפריט) ובלי ריפוד כפול;
  // הלשוניות הפנימיות = בורר מקטעים כמו בשאר המסכים.
  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      {!embedded && <h1 style={{ fontSize: 'var(--t-xl)', fontWeight: 700, margin: 0 }}>הגדרות תיוג וסיום שיחה</h1>}
      <Tabs
        items={tabs.map((t) => ({ id: t.id, label: t.label, icon: <Icon name={t.icon} size={15} /> }))}
        value={tab}
        onChange={select}
      />

      {tab === 'dispositions' && <Dispositions embedded />}
      {tab === 'tags' && <CallTags embedded />}
    </div>
  );
}
